/**
 * メンテナンス予告のカウントダウン表示を更新する
 *
 * サーバー側（MaintenanceNoticeComposer / maintenance-notice.blade.php）が初期表示の文言を描画しており、
 * このJSが無くても初期状態は正しく表示される。[data-maintenance-countdown] が無いページ（表示条件に当たらない、
 * guestレイアウト等）では何もしない。
 *
 * 読み込み時に data-server-now（サーバー時刻）と端末時計（Date.now()）の差をオフセットとして保持し、
 * 以後は「Date.now() + オフセット」をサーバー時刻とみなして残り時間を計算する。端末時計がずれていても
 * 残り時間はサーバー時刻基準になる。
 *
 * 更新は「サーバー時刻基準で分が変わる境界」に合わせてスケジュールし、以後は分が変わるたびに再計算する
 * （結果として60秒ごとの更新になる）。タブが非表示の間はブラウザがタイマーを遅延させうるため、
 * visibilitychangeで復帰したときにも即時に再計算し、タイマーを立て直す。
 * 残り時間が0以下になったら要素を隠し、以後の再計算はしない。
 *
 * 開始時刻を過ぎたら、今のページに HEAD で問い合わせ続け、503（CloudFront が返すメンテナンス画面）が返ったら
 * 読み込み直してメンテナンス画面に切り替える（lms-797 T9-9。ページを開いたままの利用者が、操作しなくても切り替わるようにする）。
 * メンテナンスモードへの切り替えは開始時刻の直後に手で行う（GitHub Actions の maintenance-prod.yml）ので、
 * 開始時刻ちょうどではなく CHECK_INTERVAL_MS ごとに問い合わせ、WATCH_DURATION_MS でやめる
 * （手順書では、開始から WATCH_DURATION_MS 以内に down することにしている）。
 * リダイレクト（ログアウト済みなど）や通信の失敗は、メンテナンスモードとはみなさず問い合わせを続ける。
 */
const MINUTE_MS = 60 * 1000
// 開始時刻を過ぎてから、メンテナンスモードになったかを今のページに問い合わせる間隔と、問い合わせ続ける時間
const CHECK_INTERVAL_MS = 10 * 1000
const WATCH_DURATION_MS = 15 * MINUTE_MS

/**
 * 残りミリ秒を「メンテナンスまで残りN日N時間N分」の文言に整形する（日が0でも「0日」と出す）
 *
 * @param {number} remaining_ms
 * @returns {string}
 */
const format_remaining = (remaining_ms) => {
    const total_minutes = Math.floor(remaining_ms / MINUTE_MS)
    const days = Math.floor(total_minutes / (24 * 60))
    const hours = Math.floor((total_minutes % (24 * 60)) / 60)
    const minutes = total_minutes % 60
    return `メンテナンスまで残り${days}日${hours}時間${minutes}分`
}

/**
 * 1つの [data-maintenance-countdown] 要素に対してカウントダウンの再計算とタイマーをセットアップする
 *
 * @param {HTMLElement} element
 * @returns {void}
 */
const setup_countdown = (element) => {
    const start_at = new Date(element.dataset.startAt)
    const server_now = new Date(element.dataset.serverNow)
    if (Number.isNaN(start_at.getTime()) || Number.isNaN(server_now.getTime())) return

    const text_element = element.querySelector('[data-maintenance-countdown-text]')

    // 読み込み時点の「サーバー時刻 - 端末時計」の差。以後はこれを使って端末時計のずれを打ち消す
    const offset_ms = server_now.getTime() - Date.now()
    const adjusted_now = () => Date.now() + offset_ms

    let timer_id = null
    let watching = false

    /**
     * 保留中のタイマーを止める
     *
     * @returns {void}
     */
    const clear_scheduled = () => {
        if (timer_id === null) return
        clearTimeout(timer_id)
        timer_id = null
    }

    /**
     * 開始時刻を過ぎたあと、メンテナンスモードになるまで今のページに問い合わせ、503 が返ったら読み込み直す（何度呼んでも1回だけ始める）
     *
     * @returns {void}
     */
    const watch_for_maintenance = () => {
        if (watching) return
        watching = true
        const give_up_at = Date.now() + WATCH_DURATION_MS
        const check = async () => {
            try {
                const response = await fetch(window.location.href, { method: 'HEAD', cache: 'no-store', redirect: 'manual' })
                if (response.status === 503) {
                    window.location.reload()
                    return
                }
            } catch (error) {
                // 通信の失敗は、メンテナンスモードとはみなさない
            }
            if (Date.now() < give_up_at) setTimeout(check, CHECK_INTERVAL_MS)
        }
        setTimeout(check, CHECK_INTERVAL_MS)
    }

    /**
     * 残り時間を再計算し、文言を書き換える
     *
     * @returns {boolean} 以後も更新を続けるべきか（残り時間が0以下になったら false）
     */
    const update = () => {
        const remaining_ms = start_at.getTime() - adjusted_now()
        if (remaining_ms <= 0) {
            element.classList.add('hidden')
            watch_for_maintenance()
            return false
        }
        if (text_element !== null) text_element.textContent = format_remaining(remaining_ms)
        return true
    }

    /**
     * サーバー時刻基準で次に分が変わる瞬間まで待ってから再計算し、以後も同様に繰り返す
     *
     * @returns {void}
     */
    const schedule_next_update = () => {
        clear_scheduled()
        const ms_into_minute = adjusted_now() % MINUTE_MS
        const delay = MINUTE_MS - ms_into_minute
        timer_id = setTimeout(() => {
            timer_id = null
            if (update()) schedule_next_update()
        }, delay)
    }

    if (update()) schedule_next_update()

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return
        if (update()) schedule_next_update()
    })
}

/**
 * 初期化
 *
 * @returns {void}
 */
const init_maintenance_countdown = () => {
    const elements = document.querySelectorAll('[data-maintenance-countdown]')
    elements.forEach((element) => setup_countdown(element))
}

document.addEventListener('DOMContentLoaded', init_maintenance_countdown)
